import styled, { css } from 'styled-components';
import colors from '@/common/view/colors';

/**
 * 공통 마크다운 기본 스타일
 * - CustomerAIGuideSection, ChatMessage 등에서 재사용
 * - 색상은 각 컴포넌트에서 오버라이드
 */
export const markdownBaseStyles = css`
  font-size: 14px;
  line-height: 1.7;
  word-break: keep-all;
  overflow-wrap: break-word;

  /* 제목 스타일 */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: 700;
    line-height: 1.4;

    &:not(:first-child) {
      margin-top: 20px;
    }
  }

  h1 { font-size: 18px; margin-bottom: 12px; }
  h2 { font-size: 16px; margin-bottom: 10px; }
  h3 { font-size: 15px; margin-bottom: 8px; }
  h4 { font-size: 14px; margin-bottom: 6px; }
  h5 { font-size: 13px; margin-bottom: 4px; }
  h6 { font-size: 12px; margin-bottom: 4px; }

  /* 단락 스타일 */
  p {
    margin: 0 0 12px 0;
    &:last-child { margin-bottom: 0; }
  }

  /* 리스트 기본 설정 */
  ul, ol {
    margin: 12px 0;
    padding-left: 0;
    list-style: none;
  }

  ul li {
    position: relative;
    padding-left: 24px;
    margin-bottom: 8px;

    &::before {
      content: '';
      position: absolute;
      left: 8px;
      top: 9px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    ul li::before {
      background: transparent;
      width: 4px;
      height: 4px;
    }
  }

  ol {
    counter-reset: list-counter;

    > li {
      position: relative;
      padding-left: 24px;
      margin-bottom: 10px;
      counter-increment: list-counter;

      &::before {
        position: absolute;
        left: 0;
        top: 0;
        font-size: 14px;
        font-weight: 600;
      }
    }

    ol {
      counter-reset: list-counter-nested;
      margin: 8px 0 8px 8px;

      > li {
        padding-left: 24px;
        margin-bottom: 6px;
        counter-increment: list-counter-nested;

        &::before {
          font-size: 13px;
          font-weight: 500;
        }
      }
    }
  }

  /* hr 스타일 */
  hr {
    border: none;
    height: 1px;
    margin: 24px 0;
  }

  /* 코드 스타일 */
  code {
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
    font-size: 13px;
  }

  pre {
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0;

    code {
      background: transparent;
      padding: 0;
      font-size: 12px;
      line-height: 1.5;
    }
  }

  /* 강조 스타일 */
  strong { font-weight: 700; }
  em { font-style: italic; }

  /* 링크 스타일 */
  a {
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { text-decoration-thickness: 2px; }
  }
`;

// 코드 블록 배경색 (colors에 정의되지 않은 특수 케이스)
const CODE_BLOCK_BG = {
  user: 'rgba(0, 0, 0, 0.3)',
  assistant: colors.gray100,
} as const;

interface MarkdownBubbleProps {
  $isUser: boolean;
}

export const MarkdownBubble = styled.div<MarkdownBubbleProps>`
  max-width: 100%;
  padding: 12px 16px;
  border-radius: 16px;
  border-top-${(props) => (props.$isUser ? 'right' : 'left')}-radius: 4px;
  background: ${(props) => (props.$isUser ? colors.blue50 : 'white')};
  border: ${(props) => (props.$isUser ? 'none' : `1px solid ${colors.gray20}`)};
  box-shadow: ${(props) => (props.$isUser ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.05)')};
  color: ${(props) => (props.$isUser ? 'white' : colors.gray100)};
  font-size: 14px;
  line-height: 1.7;
  word-break: keep-all;
  overflow-wrap: break-word;

  /* 제목 스타일 */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: 700;
    line-height: 1.4;

    &:not(:first-child) {
      margin-top: 20px;
    }
  }

  h1 {
    font-size: 18px;
    padding: 12px 16px;
    margin-bottom: 16px;
    background: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.15)' : colors.blue20};
    border-left: 4px solid ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.5)' : colors.blue50};
    border-radius: 0 8px 8px 0;
  }

  h2 {
    font-size: 16px;
    padding-bottom: 8px;
    margin-bottom: 12px;
    border-bottom: 2px solid ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.3)' : colors.blue30};
  }

  h3 {
    font-size: 15px;
    padding-left: 12px;
    margin-bottom: 8px;
    border-left: 3px solid ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.5)' : colors.blue50};
  }

  h4 {
    font-size: 14px;
    color: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.9)' : colors.gray100};
    margin-bottom: 6px;
  }

  h5 {
    font-size: 13px;
    color: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.85)' : colors.gray50};
    margin-bottom: 4px;
  }

  h6 {
    font-size: 12px;
    color: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.8)' : colors.gray40};
    margin-bottom: 4px;
  }

  /* 단락 스타일 */
  p {
    margin: 0 0 12px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* 리스트 스타일 */
  ul, ol {
    margin: 12px 0;
    padding-left: 0;
    list-style: none;
  }

  ul li {
    position: relative;
    padding-left: 24px;
    margin-bottom: 8px;

    &::before {
      content: '';
      position: absolute;
      left: 8px;
      top: 9px;
      width: 6px;
      height: 6px;
      background: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.8)' : colors.blue50};
      border-radius: 50%;
    }

    /* 중첩 리스트 스타일 */
    ul li::before {
      background: transparent;
      border: 2px solid ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.6)' : colors.blue30};
      width: 4px;
      height: 4px;
    }
  }

  ol {
    counter-reset: list-counter;

    > li {
      position: relative;
      padding-left: 32px;
      margin-bottom: 10px;
      counter-increment: list-counter;

      &::before {
        content: counter(list-counter);
        position: absolute;
        left: 0;
        top: 0;
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.2)' : colors.blue20};
        color: ${(props) => props.$isUser ? 'white' : colors.blue70};
        font-size: 12px;
        font-weight: 600;
        border-radius: 6px;
      }
    }

    /* 중첩 ol 리스트 - 작고 연한 번호 배지 */
    ol {
      counter-reset: list-counter-nested;
      margin: 8px 0 8px 8px;

      > li {
        padding-left: 28px;
        margin-bottom: 6px;
        counter-increment: list-counter-nested;

        &::before {
          content: counter(list-counter-nested);
          width: 18px;
          height: 18px;
          top: 2px;
          left: 0;
          background: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.15)' : colors.gray20};
          color: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.8)' : colors.gray50};
          font-size: 11px;
          font-weight: 500;
        }
      }
    }
  }

  /* blockquote 스타일 */
  blockquote {
    margin: 16px 0;
    padding: 16px 20px;
    background: ${(props) => props.$isUser ? 'rgba(0, 0, 0, 0.15)' : colors.blue10};
    border-left: 4px solid ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.5)' : colors.blue50};
    border-radius: 0 8px 8px 0;
    font-style: italic;

    p {
      margin: 0;
    }
  }

  /* hr 스타일 */
  hr {
    border: none;
    height: 1px;
    margin: 24px 0;
    background: linear-gradient(
      90deg,
      transparent,
      ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.3)' : colors.gray30} 20%,
      ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.3)' : colors.gray30} 80%,
      transparent
    );
  }

  /* 코드 스타일 */
  code {
    background: ${(props) => (props.$isUser ? 'rgba(255, 255, 255, 0.2)' : colors.gray20)};
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
    font-size: 13px;
  }

  pre {
    background: ${(props) => (props.$isUser ? CODE_BLOCK_BG.user : CODE_BLOCK_BG.assistant)};
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0;

    code {
      background: transparent;
      padding: 0;
      color: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.9)' : colors.gray10};
      font-size: 12px;
      line-height: 1.5;
    }
  }

  /* 테이블은 숨김 - 패널에서 표시 */
  table {
    display: none;
  }

  /* 강조 스타일 */
  strong {
    font-weight: 700;
    color: ${(props) => props.$isUser ? 'white' : colors.gray100};
  }

  em {
    font-style: italic;
    color: ${(props) => props.$isUser ? 'rgba(255, 255, 255, 0.9)' : colors.gray50};
  }

  /* 링크 스타일 */
  a {
    color: ${(props) => (props.$isUser ? 'white' : colors.blue70)};
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      text-decoration-thickness: 2px;
    }
  }
`;
